import React, { useState } from 'react';
import { uploadService } from '../../../services/uploadService';

const CruiseForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    carrier: initialData?.carrier || '',
    ship: initialData?.ship || '',
    stateroom: initialData?.stateroom || '',
    departsFrom: initialData?.departsFrom || '',
    departureDate: initialData?.departureDate || '',
    departureTime: initialData?.departureTime || '',
    numberOfPorts: initialData?.numberOfPorts || '',
    arrivesIn: initialData?.arrivesIn || '',
    arrivalDate: initialData?.arrivalDate || '',
    arrivalTime: initialData?.arrivalTime || '',
    confirmationNumber: initialData?.confirmationNumber || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price) || 0
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    try {
      // Upload each file to the server
      const uploadPromises = files.map(async (file) => {
        const response = await uploadService.uploadFile(file, 'quote-photos');
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          filename: response.filename,
          url: uploadService.getFileUrl(response.filename),
          size: response.size
        };
      });
      
      const uploadedPhotos = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedPhotos]
      }));
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    try {
      // Upload each file to the server
      const uploadPromises = files.map(async (file) => {
        const response = await uploadService.uploadFile(file, 'quote-documents');
        return {
          id: Date.now() + Math.random(),
          name: file.name,
          filename: response.filename,
          url: uploadService.getFileUrl(response.filename),
          size: response.size
        };
      });
      
      const uploadedDocs = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, ...uploadedDocs]
      }));
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Failed to upload documents. Please try again.');
    }
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const removeDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== docId)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="component-form cruise-form">
      <div className="form-section">
        <h4>Cruise Information</h4>
        
        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., 7-Day Caribbean Cruise"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Cruise Line*</label>
            <input
              type="text"
              value={formData.carrier}
              onChange={(e) => setFormData({...formData, carrier: e.target.value})}
              placeholder="e.g., Royal Caribbean"
              required
            />
          </div>
          <div className="form-group">
            <label>Ship Name*</label>
            <input
              type="text"
              value={formData.ship}
              onChange={(e) => setFormData({...formData, ship: e.target.value})}
              placeholder="e.g., Symphony of the Seas"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Stateroom Type</label>
            <input
              type="text"
              value={formData.stateroom}
              onChange={(e) => setFormData({...formData, stateroom: e.target.value})}
              placeholder="e.g., Balcony Stateroom"
            />
          </div>
          <div className="form-group">
            <label>Number of Ports</label>
            <input
              type="number"
              value={formData.numberOfPorts}
              onChange={(e) => setFormData({...formData, numberOfPorts: e.target.value})}
              placeholder="e.g., 4"
              min="0"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Departure Information</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Departs From*</label>
            <input
              type="text"
              value={formData.departsFrom}
              onChange={(e) => setFormData({...formData, departsFrom: e.target.value})}
              placeholder="e.g., Port Canaveral, FL"
              required
            />
          </div>
          <div className="form-group">
            <label>Departure Date*</label>
            <input
              type="date"
              value={formData.departureDate}
              onChange={(e) => setFormData({...formData, departureDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Departure Time</label>
            <input
              type="time"
              value={formData.departureTime}
              onChange={(e) => setFormData({...formData, departureTime: e.target.value})}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Arrival Information</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Arrives In*</label>
            <input
              type="text"
              value={formData.arrivesIn}
              onChange={(e) => setFormData({...formData, arrivesIn: e.target.value})}
              placeholder="e.g., Port Canaveral, FL"
              required
            />
          </div>
          <div className="form-group">
            <label>Arrival Date*</label>
            <input
              type="date"
              value={formData.arrivalDate}
              onChange={(e) => setFormData({...formData, arrivalDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Arrival Time</label>
            <input
              type="time"
              value={formData.arrivalTime}
              onChange={(e) => setFormData({...formData, arrivalTime: e.target.value})}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Booking Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmationNumber}
              onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
              placeholder="e.g., ABC123456"
            />
          </div>
          <div className="form-group">
            <label>Price*</label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Additional Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Add any additional details about this cruise..."
            rows="3"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Attachments</h4>
        
        <div className="form-group">
          <label>Photos</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            className="file-input"
          />
          {formData.photos.length > 0 && (
            <div className="file-list">
              {formData.photos.map(photo => (
                <div key={photo.id} className="file-item">
                  <span>{photo.name}</span>
                  <button type="button" onClick={() => removePhoto(photo.id)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Documents</label>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={handleDocumentUpload}
            className="file-input"
          />
          {formData.documents.length > 0 && (
            <div className="file-list">
              {formData.documents.map(doc => (
                <div key={doc.id} className="file-item">
                  <span>{doc.name}</span>
                  <button type="button" onClick={() => removeDocument(doc.id)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Cruise
        </button>
      </div>
    </form>
  );
};

export default CruiseForm;
